import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Input,
  Textarea,
  Select,
  Switch,
  Button,
  Badge,
  IconButton,
} from "../ui";
import { Plus, X } from "lucide-react";
import {
  createVehicle,
  updateVehicle,
  createVehiclePortal,
  updateVehiclePortal,
  getAllPincodeRates,
  getAllPincodeRatesPortal,
} from "../../api/transport/transportApi";
import { useToast } from "../../context/ToastContext";

const VEHICLE_TYPE_OPTIONS = [
  { label: "Bus", value: "BUS" },
  { label: "Van", value: "VAN" },
  { label: "Mini Bus", value: "MINI_BUS" },
  { label: "Other", value: "OTHER" },
];

const VehicleModal = ({
  isOpen,
  onClose,
  onSuccess,
  editData = null,
  isPortal = false,
}) => {
  const [form, setForm] = useState({
    vehicleType: "BUS",
    name: "",
    vehicleNumber: "",
    driverName: "",
    driverPhone: "",
    conductorName: "",
    conductorPhone: "",
    pincodes: [],
    routeDescription: "",
    capacity: "",
    isActive: true,
    remarks: "",
  });
  const toast = useToast();

  /** Pincode currently chosen in the dropdown (before Add) */
  const [pincodeToAdd, setPincodeToAdd] = useState("");
  /** Options from Transport Pincode Rate master */
  const [ratePincodeOptions, setRatePincodeOptions] = useState([]);
  const [loadingRates, setLoadingRates] = useState(false);
  const [loading, setLoading] = useState(false);
  // Load unique pincodes from price list
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      setLoadingRates(true);
      try {
        const fn = isPortal ? getAllPincodeRatesPortal : getAllPincodeRates;
        const res = await fn({ isActive: "true" });
        const rates = res.data || res || [];
        const map = new Map();
        rates.forEach((r) => {
          const pin = String(r.pincode || "").trim();
          if (!pin) return;
          if (!map.has(pin)) {
            map.set(pin, {
              value: pin,
              label: r.areaName ? `${pin} — ${r.areaName}` : pin,
            });
          }
        });
        setRatePincodeOptions(
          Array.from(map.values()).sort((a, b) =>
            a.value.localeCompare(b.value)
          )
        );
      } catch {
        setRatePincodeOptions([]);
      } finally {
        setLoadingRates(false);
      }
    })();
  }, [isOpen, isPortal]);

  useEffect(() => {
    if (!isOpen) return;
    setPincodeToAdd("");
    if (editData) {
      setForm({
        vehicleType: editData.vehicleType || "BUS",
        name: editData.name || "",
        vehicleNumber: editData.vehicleNumber || "",
        driverName: editData.driverName || "",
        driverPhone: editData.driverPhone || "",
        conductorName: editData.conductorName || "",
        conductorPhone: editData.conductorPhone || "",
        pincodes: Array.isArray(editData.pincodes)
          ? [...editData.pincodes]
          : [],
        routeDescription: editData.routeDescription || "",
        capacity: editData.capacity ?? "",
        isActive: editData.isActive !== false,
        remarks: editData.remarks || "",
      });
    } else {
      setForm({
        vehicleType: "BUS",
        name: "",
        vehicleNumber: "",
        driverName: "",
        driverPhone: "",
        conductorName: "",
        conductorPhone: "",
        pincodes: [],
        routeDescription: "",
        capacity: "",
        isActive: true,
        remarks: "",
      });
    }
  }, [isOpen, editData]);

  // Dropdown only shows pincodes not already selected
  const availablePincodeOptions = useMemo(() => {
    const selected = new Set(form.pincodes.map(String));
    return ratePincodeOptions.filter((o) => !selected.has(String(o.value)));
  }, [ratePincodeOptions, form.pincodes]);

  const labelForPincode = (pin) => {
    const found = ratePincodeOptions.find((o) => String(o.value) === String(pin));
    return found?.label || pin;
  };

  const handleAddPincode = () => {
    if (!pincodeToAdd) return;
    const pin = String(pincodeToAdd).trim();
    if (!pin) return;
    if (form.pincodes.map(String).includes(pin)) {
      setPincodeToAdd("");
      return;
    }
    // Only allow pincodes that exist in the rate master
    const allowed = ratePincodeOptions.some((o) => String(o.value) === pin);
    if (!allowed) {
      toast.error("Select a pincode from the defined pincode price list.");
      return;
    }
    setForm((p) => ({ ...p, pincodes: [...p.pincodes, pin] }));
    setPincodeToAdd("");
  };

  const handleRemovePincode = (pin) => {
    setForm((p) => ({
      ...p,
      pincodes: p.pincodes.filter((x) => String(x) !== String(pin)),
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      if (!form.pincodes.length) {
        toast.error("Add at least one pincode from the pincode price list.");
        setLoading(false);
        return;
      }

      const payload = {
        vehicleType: form.vehicleType,
        name: form.name.trim(),
        vehicleNumber: form.vehicleNumber.trim(),
        driverName: form.driverName.trim(),
        driverPhone: form.driverPhone.trim(),
        conductorName: form.conductorName.trim(),
        conductorPhone: form.conductorPhone.trim(),
        pincodes: form.pincodes,
        routeDescription: form.routeDescription,
        capacity: Number(form.capacity),
        isActive: form.isActive,
        remarks: form.remarks,
      };

      if (editData?._id) {
        if (isPortal) await updateVehiclePortal(editData._id, payload);
        else await updateVehicle(editData._id, payload);
      } else {
        if (isPortal) await createVehiclePortal(payload);
        else await createVehicle(payload);
      }
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save vehicle");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editData ? "Edit Vehicle" : "Add Vehicle"}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={loading}>
            {editData ? "Update" : "Create"}
          </Button>
        </>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="space-y-4 max-h-[70vh] overflow-y-auto pr-1"
      >
        

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Vehicle type"
            options={VEHICLE_TYPE_OPTIONS}
            value={form.vehicleType}
            onChange={(v) => setForm((p) => ({ ...p, vehicleType: v }))}
          />
          <Input
            label="Capacity (seats)"
            type="number"
            min={1}
            value={form.capacity}
            onChange={(e) =>
              setForm((p) => ({ ...p, capacity: e.target.value }))
            }
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Vehicle name / label"
            placeholder="e.g. Bus Route A"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            required
          />
          <Input
            label="Vehicle number"
            placeholder="e.g. DL-01-AB-1234"
            value={form.vehicleNumber}
            onChange={(e) =>
              setForm((p) => ({ ...p, vehicleNumber: e.target.value }))
            }
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Driver name"
            value={form.driverName}
            onChange={(e) =>
              setForm((p) => ({ ...p, driverName: e.target.value }))
            }
          />
          <Input
            label="Driver phone"
            value={form.driverPhone}
            onChange={(e) =>
              setForm((p) => ({ ...p, driverPhone: e.target.value }))
            }
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Conductor / helper name"
            value={form.conductorName}
            onChange={(e) =>
              setForm((p) => ({ ...p, conductorName: e.target.value }))
            }
          />
          <Input
            label="Conductor phone"
            value={form.conductorPhone}
            onChange={(e) =>
              setForm((p) => ({ ...p, conductorPhone: e.target.value }))
            }
          />
        </div>

        {/* Pincodes: select from price list + Add (no comma input) */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
            Pincodes served
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Choose from pincodes already defined in the Pincode Price list.
          </p>

          <div className="flex flex-col sm:flex-row gap-2 items-end">
            <div className="flex-1 w-full">
              <Select
                label="Select pincode"
                options={availablePincodeOptions}
                value={pincodeToAdd}
                onChange={setPincodeToAdd}
                placeholder={
                  loadingRates
                    ? "Loading pincodes..."
                    : availablePincodeOptions.length
                      ? "Search or select pincode"
                      : ratePincodeOptions.length
                        ? "All listed pincodes already added"
                        : "No pincode rates found"
                }
                searchable
                disabled={loadingRates || availablePincodeOptions.length === 0}
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={handleAddPincode}
              disabled={!pincodeToAdd || loadingRates}
            >
              Add
            </Button>
          </div>

          {!loadingRates && ratePincodeOptions.length === 0 && (
            <p className="text-xs text-warning-700">
              No active pincode rates found. Add them under Transport → Pincode
              Rates first.
            </p>
          )}

          {form.pincodes.length > 0 ? (
            <div className="flex flex-wrap gap-2 pt-1">
              {form.pincodes.map((pin) => (
                <span
                  key={pin}
                  className="inline-flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 pl-2.5 pr-1 py-0.5 text-sm text-primary-900 dark:text-primary-300"
                >
                  <Badge variant="primary">{labelForPincode(pin)}</Badge>
                  <IconButton
                    aria-label={`Remove pincode ${pin}`}
                    variant="ghost"
                    onClick={() => handleRemovePincode(pin)}
                    className="!h-6 !w-6"
                  >
                    <X className="h-3.5 w-3.5" />
                  </IconButton>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-neutral-500 dark:text-neutral-400">No pincodes added yet.</p>
          )}
        </div>

        <Textarea
          label="Bus route description"
          rows={3}
          placeholder="Describe the route / stops"
          value={form.routeDescription}
          onChange={(e) =>
            setForm((p) => ({ ...p, routeDescription: e.target.value }))
          }
        />

        <Input
          label="Remarks"
          value={form.remarks}
          onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))}
        />

        <Switch
          label="Active"
          checked={form.isActive}
          onChange={(checked) =>
            setForm((p) => ({ ...p, isActive: checked }))
          }
        />
      </form>
    </Modal>
  );
};

export default VehicleModal;
